class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.boundClose = this.close.bind(this);
    this.renderVersion = 0;
    this.setAttribute("aria-hidden", this.classList.contains("active") ? "false" : "true");
    this.inert = !this.classList.contains("active");
    this.drawerClassObserver = new MutationObserver(() => this.syncInteractionState());
    this.drawerClassObserver.observe(this, { attributes: true, attributeFilter: ["class"] });
    this.syncInteractionState();
    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.addEventListener("cart-drawer:update", () => this.releaseInteractionLocks());
    this.addEventListener("cart-drawer:render", () => this.releaseInteractionLocks());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
    this.bindContinueShoppingButton();
    this.bindUpsellButton();
  }

  bindOverlay() {
    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (!overlay || overlay.dataset.cartDrawerBound === "true") return;
    overlay.dataset.cartDrawerBound = "true";
    overlay.addEventListener("click", this.boundClose);
  }

  releaseInteractionLocks() {
    document.querySelectorAll(".cart__items--disabled").forEach((element) => {
      element.classList.remove("cart__items--disabled");
    });
  }

  syncInteractionState() {
    const isOpen = this.classList.contains("active");
    // Only the inner panel should animate. A visibility transition on the
    // viewport shell can remain at `hidden` when cart renders overlap.
    this.style.transitionProperty = "none";
    this.setAttribute("aria-hidden", isOpen ? "false" : "true");
    this.inert = !isOpen;

    if (isOpen) {
      this.style.pointerEvents = "auto";
      this.style.visibility = "visible";
      return;
    }

    this.style.pointerEvents = "none";
    this.style.visibility = "hidden";
    document.body?.classList.remove("overflow-hidden");
    this.releaseInteractionLocks();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  bindContinueShoppingButton() {
    this.addEventListener("click", (event) => {
      const continueShoppingButton = event.target.closest("[data-cart-drawer-continue-shopping]");
      if (!continueShoppingButton) return;

      event.preventDefault();
      this.close();
    });
  }

  bindUpsellButton() {
    this.addEventListener("click", (event) => {
      const upsellButton = event.target.closest(".upsell-add-button");
      if (upsellButton) {
        event.preventDefault();

        const form = upsellButton.closest(".upsell-form");
        const variantId = form.querySelector('input[name="id"]').value;

        this.addItemToCart(variantId);
      }
    });
  }

  async addItemToCart(variantId) {
    const sections = this.getSectionsToRender().map((section) => section.id);

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: sections,
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${routes.cart_add_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: `application/json`,
        },
        body,
      });

      if (response.status === 422) {
        const errorData = await response.json();
        // Handle error (e.g., show a notification)
        console.error("Error adding item to cart:", errorData.description);
        return;
      }

      const parsedState = await response.json();
      await this.renderContents(parsedState);
    } catch (error) {
      console.error("Error adding item to cart:", error);
    }
  }

  /**
   * Refresh cart drawer after AJAX cart operations
   * Can be called directly or will be triggered by 'cart:ajax-update' event
   * @param {Object} sectionsData - Optional sections data from cart response
   */
  async refreshCartDrawer(sectionsData = null) {
    if (sectionsData && sectionsData.sections) {
      const itemCount = this.getItemCount(sectionsData);
      const dataToRender = {
        ...sectionsData,
        id: sectionsData.id || this.productId || null,
        item_count: itemCount
      };
      await this.renderContents(dataToRender);
      return;
    }

    try {
      const sections = this.getSectionsToRender().map((section) => section.id);
      const url = `${window.location.pathname}?sections=${sections.join(',')}`;
      const response = await fetch(url, { credentials: "same-origin", cache: "no-store" });
      
      if (!response.ok) {
        return;
      }

      const parsedState = await response.json();
      
      // Wrap return in sections object for renderContents
      let sectionsData = null;
      if (parsedState.sections) {
        sectionsData = parsedState.sections;
      } else {
        sectionsData = {};
        const sectionIds = this.getSectionsToRender().map((section) => section.id);
        sectionIds.forEach((sectionId) => {
          if (parsedState[sectionId]) {
            sectionsData[sectionId] = parsedState[sectionId];
          }
        });
      }

      if (!sectionsData || Object.keys(sectionsData).length === 0) {
        console.error("No sections found in response:", parsedState);
        return;
      }

      const itemCount = this.getItemCount({ ...parsedState, sections: sectionsData });
      const dataToRender = {
        sections: sectionsData,
        id: parsedState.id || this.productId || null,
        item_count: itemCount
      };
      
      await this.renderContents(dataToRender);
    } catch (error) {
      console.error("Error refreshing cart drawer:", error);
    }
  }

  getItemCount(state) {
    const directCount = Number.parseInt(state?.item_count, 10);
    if (Number.isFinite(directCount) && directCount >= 0) return directCount;

    const drawerHtml = state?.sections?.["cart-drawer"];
    if (!drawerHtml) return null;

    const drawerRoot = new DOMParser().parseFromString(drawerHtml, "text/html").querySelector("#CartDrawer");
    const sectionCount = Number.parseInt(drawerRoot?.dataset.cartItemCount, 10);
    return Number.isFinite(sectionCount) && sectionCount >= 0 ? sectionCount : null;
  }

  startCountdownTimer() {
    const timerElement = document.getElementById("cart-countdown-timer");
    if (!timerElement) return;

    // Reset timer to 10 minutes
    let minutes = 10;
    let seconds = 0;

    // Clear any existing timer
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    // Update the timer display
    const updateTimer = () => {
      timerElement.textContent = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    // Initial display
    updateTimer();

    // Start the countdown
    this.countdownInterval = setInterval(() => {
      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(this.countdownInterval);
          return;
        }
        minutes--;
        seconds = 59;
      } else {
        seconds--;
      }

      updateTimer();
    }, 1000);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.releaseInteractionLocks();
    this.inert = false;
    this.style.removeProperty("pointer-events");
    this.style.removeProperty("visibility");
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);
    // Opening is one atomic state change. Deferring `active` left a window in
    // which a concurrent cart update could mark the drawer inert again.
    this.classList.add("animate", "active");
    this.setAttribute("aria-hidden", "false");
    this.syncInteractionState();

    // The viewport shell has no transition. Waiting for a bubbling descendant
    // transition leaves stale listeners that can re-enable a focus trap after
    // the drawer has closed. Arm focus on the next frame and verify it is open.
    cancelAnimationFrame(this.focusTrapFrame);
    this.focusTrapFrame = requestAnimationFrame(() => {
      if (!this.classList.contains("active")) return;
      const containerToTrapFocusOn = this.classList.contains("is-empty")
        ? this.querySelector(".drawer__inner-empty")
        : this.querySelector("#CartDrawer");
      const focusElement =
        this.querySelector(".drawer__close") ||
        this.querySelector("[data-cart-drawer-continue-shopping]") ||
        this.querySelector(".drawer__inner");
      if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
    });

    document.body.classList.add("overflow-hidden");

    // Check if jQuery and slick are loaded before initializing
    if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
      const $slider = $(".gbfrequently-bought-with-main-whole-slider");
      if ($slider.length > 0) {
        $slider.slick({
          infinite: true,
          autoplaySpeed: 1000,
          autoplay: false,
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        });
        $slider.css("opacity", "1");
      }
    }

    // Start the countdown timer when drawer opens
    this.startCountdownTimer();

    this.dispatchEvent(new CustomEvent("cart-drawer:open", {
      bubbles: true,
      detail: { triggeredBy }
    }));
  }

  close() {
    this.renderVersion += 1;
    cancelAnimationFrame(this.focusTrapFrame);
    // Check if cart customization mode is active
    const cartCustomizationMode = this.classList.contains("customization-mode");
    if (cartCustomizationMode) {
      alert("Cart Customization Mode is active. Disable it in Theme Settings > Cart Drawer to allow closing.");
      return;
    }

    this.classList.remove("active");
    try {
      removeTrapFocus(this.activeElement || document.activeElement);
    } catch (error) {
      console.warn("Unable to restore cart drawer focus:", error);
    } finally {
      this.setAttribute("aria-hidden", "true");
      document.body.classList.remove("overflow-hidden");
      this.releaseInteractionLocks();
      this.inert = true;
      this.style.pointerEvents = "none";
      this.style.visibility = "hidden";

      // Clear the countdown timer when drawer closes
      if (this.countdownInterval) clearInterval(this.countdownInterval);
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  async renderContents(parsedState, options = {}) {
    const renderVersion = ++this.renderVersion;
    const shouldOpen = options.open !== false;
    const drawerInner = this.querySelector(".drawer__inner");
    drawerInner?.classList.remove("is-empty");
    this.productId = parsedState.id;
    let renderState = parsedState;

    for (const section of this.getSectionsToRender()) {
      const sectionHtml = renderState.sections?.[section.id];
      if (!sectionHtml) continue;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) continue;
      if (section.id == "cart-drawer" || section.selector == "#CartDrawer") {
        const newCartDrawer = this.getSectionDOM(sectionHtml, section.selector);
        const updateResult = updateCartDrawer(sectionElement, newCartDrawer);
        if (updateResult && typeof updateResult.then === "function") {
          const reconciledState = await updateResult;
          if (renderVersion !== this.renderVersion) return;
          if (reconciledState?.sections) renderState = reconciledState;
        }
      } else {
        if (section.id === "cart-icon-bubble") {
          const iconType = sectionElement.getAttribute("data-cart-icon-type");
          const sectionContents = this.getSectionInnerHTML(sectionHtml, section.selector);
          if (sectionContents !== null) sectionElement.innerHTML = sectionContents;
          if (iconType) {
            sectionElement.setAttribute("data-cart-icon-type", iconType);
          }
          const iconWrapper = sectionElement.querySelector(".svg-wrapper");
          if (iconWrapper && iconType) {
            const bagIcon = iconWrapper.querySelector(".cart-icon-bag");
            const cartIcon = iconWrapper.querySelector(".cart-icon-cart");
            const cartIconEmpty = iconWrapper.querySelector(".cart-icon-cart-empty");
            
            if (iconType === "bag") {
              if (bagIcon) bagIcon.style.display = "flex";
            } 
            else {
              const cartEmpty = renderState.item_count === 0;
              if (cartEmpty && cartIconEmpty) {
                cartIconEmpty.style.display = "flex";
              } else if (cartIcon) {
                cartIcon.style.display = "flex";
              }
            }
          }
        } else {
          const sectionContents = this.getSectionInnerHTML(sectionHtml, section.selector);
          if (sectionContents !== null) sectionElement.innerHTML = sectionContents;
        }
      }
    }

    setTimeout(() => {
      if (renderVersion !== this.renderVersion) return;
      this.bindOverlay();
      
      this.dispatchEvent(new CustomEvent("cart-drawer:render", {
        bubbles: true,
        detail: { parsedState: renderState }
      }));
      
      if (shouldOpen) this.open();
    });

    // Check if jQuery and slick are loaded before initializing
    if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
      const $slider = $(".gbfrequently-bought-with-main-whole-slider");
      if ($slider.length > 0) {
        $slider.slick({
          infinite: true,
          autoplaySpeed: 1000,
          autoplay: false,
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        });
        $slider.css("opacity", "1");
      }
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const section = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
    return section?.innerHTML ?? null;
  }

  getSectionsToRender() {
    setTimeout(function () {
      // Check if jQuery and slick are loaded before initializing
      if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
        var $slider = $(".gbfrequently-bought-with-main-whole-slider");
        if ($slider.length > 0 && !$slider.hasClass("slick-initialized")) {
          $slider.slick({
            infinite: true,
            autoplaySpeed: 1000,
            autoplay: false,
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
            dots: true,
            // adaptiveHeight: true
            //prevArrow: $('.our-customer-slider-prev'),
            //nextArrow: $('.our-customer-slider-next'),
          });
          $slider.css("opacity", "1");
        }
      }
    }, 500);

    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get("cart-drawer")) customElements.define("cart-drawer", CartDrawer);

function setupAjaxCartHandler() {
  const cartDrawer = document.querySelector("cart-drawer");
  if (!cartDrawer) return;

  document.addEventListener("cart:ajax-update", async function (event) {
    const sectionsData = event.detail?.sectionsData || null;
    await cartDrawer.refreshCartDrawer(sectionsData);
  });

  window.refreshCartDrawer = async function (sectionsData = null) {
    const drawer = document.querySelector("cart-drawer");
    if (drawer) {
      await drawer.refreshCartDrawer(sectionsData);
    }
  };
}

if (document.readyState === 'loading') {
  document.addEventListener("DOMContentLoaded", setupAjaxCartHandler);
} else {
  setupAjaxCartHandler();
}

// Wait for CartItems to be defined before extending it
if (typeof CartItems !== "undefined") {
  // CartItems is already loaded
  defineCartDrawerItems();
} else {
  // Wait for cart.js to load and define CartItems
  const checkCartItems = setInterval(() => {
    if (typeof CartItems !== "undefined") {
      clearInterval(checkCartItems);
      defineCartDrawerItems();
    }
  }, 50);
}

function defineCartDrawerItems() {
  if (customElements.get("cart-drawer-items")) {
    return; // Already defined
  }

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      setTimeout(function () {
        // Check if jQuery and slick are loaded before initializing
        if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
          const $slider = $(".gbfrequently-bought-with-main-whole-slider");
          if ($slider.length > 0) {
            $slider.slick({
              infinite: true,
              autoplaySpeed: 1000,
              autoplay: false,
              slidesToShow: 1,
              slidesToScroll: 1,
              arrows: false,
              dots: true,
            });
            $slider.css("opacity", "1");
          }
        }
      }, 500);

      return [
        {
          id: "CartDrawer",
          section: "cart-drawer",
          selector: ".drawer__inner",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
      ];
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);
}
